import { inject, Injectable } from '@angular/core';
import { Http, QueryHandlerFor, RootContext } from '@skylab-angular-shared/core';
import { CompanyParametersQuery, CurrencyCollectionQuery, PagedCollectionQuery, PagedCollectionQueryResult, PagedCollectionQueryResultDTO, UnitQuery, VatRateForSaleQuery } from '@skylab-shared/business';
import { HttpService, QueryHandler, RootContextBase, TaggedUrl, Tags } from '@skylab-shared/core';
import { ProductKind } from '@skylab-shared/dataModel';

export type ProductGridItem = {
    readonly id: number;
    readonly externalId: string;
    readonly version: string;
    readonly favourite: boolean;
    readonly active: boolean;
    readonly cnCode: string;
    readonly currencySymbol: string;
    readonly currencyPrecision: number;
    readonly kind: ProductKind;
    readonly name: string;
    readonly grossPrice: number;
    readonly netPrice: number;
    readonly unitSymbol: string;
    readonly vatRateSymbol: string;
    readonly inventoryLevel: number;
    readonly inventoryControl: boolean;
    readonly symbol?: string;
    readonly barCode?: string;
};

class ProductCollectionItemDTO {
    readonly active: boolean;
    readonly cnCode: string;
    readonly currency: string;
    readonly description: string;
    readonly favourite: boolean;
    readonly fiscalName: string;
    readonly id: number;
    readonly externalId: string;
    readonly kind: ProductKind;
    readonly name: string;
    readonly pkwiu: string;
    readonly groupId: number;
    readonly netPrice: number;
    readonly grossPrice: number;
    readonly currencyId: number;
    readonly subjectToReverseCharge: boolean;
    readonly unitId: number;
    readonly vatRateId: number;
    readonly version: string;
    readonly inventoryLevel: number;
    readonly inventoryControl: boolean;
    readonly symbol?: string;
    readonly barCode?: string;
}

export class ProductGridCollectionQuery extends PagedCollectionQuery<ProductGridItem> { }


@Injectable({ providedIn: 'root'})
@QueryHandlerFor(ProductGridCollectionQuery)
export class ProductGridCollectionQueryHandler implements QueryHandler<ProductGridCollectionQuery, PagedCollectionQueryResult<ProductGridItem>> {
    #Url = TaggedUrl.join('products', [Tags.Api]);
    #http =  inject(Http) as HttpService;
    #rootContext =  inject(RootContext) as any as RootContextBase;

    async handleAsync(query: ProductGridCollectionQuery): Promise<PagedCollectionQueryResult<ProductGridItem>> {
        const { data } = await this.#http.get(this.#Url, {
            params: { ...query.params },
            dtoObject: new PagedCollectionQueryResultDTO<ProductCollectionItemDTO>(ProductCollectionItemDTO)
        });

        const { systemCurrency } = await this.#rootContext.getAsync(CompanyParametersQuery);
        const { precision: currencyPrecision } = await this.#rootContext.firstAsync(CurrencyCollectionQuery, { symbol: systemCurrency });

        const items = await Promise.all(data.items.map<Promise<ProductGridItem>>(async item => {
            const vatRate = await this.#rootContext.firstAsync(VatRateForSaleQuery, { id: item.vatRateId });
            const unit = await this.#rootContext.firstAsync(UnitQuery, { id: item.unitId });

            return {
                id: item.id,
                externalId: item.externalId,
                version: item.version,
                active: item.active,
                cnCode: item.cnCode,
                currencySymbol: systemCurrency,
                currencyPrecision: currencyPrecision,
                favourite: item.favourite,
                kind: item.kind,
                name: item.name,
                vatRateSymbol: vatRate?.symbol,
                unitSymbol: unit?.symbol,
                grossPrice: item.grossPrice,
                netPrice: item.netPrice,
                inventoryControl: item.inventoryControl,
                inventoryLevel: item.inventoryLevel,
                barCode: item.barCode,
                symbol: item.symbol
            };
        }));

        return {
            items: items,
            totalItemsCount: data.totalItemsCount
        };
    }
}
